import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { supabase } from "@lib/supabase/client";
import { Button } from "@shared/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/components/ui/card";
import { Input } from "@shared/components/ui/input";
import { Textarea } from "@shared/components/ui/textarea";
import { Label } from "@shared/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@shared/components/ui/avatar";
import { toast } from "sonner";
import {
  ArrowRight, Mail, Phone, MessageCircle, Clock,
  Send, CheckCircle
} from "lucide-react";
import { z } from "zod";
import ClinicTestimonialsSection from "@features/clinic/components/ClinicTestimonialsSection";
import { readClinicPublicCache, writeClinicPublicCache } from "@features/clinic/lib/clinicPublicCache";

interface PageConfig {
  enabled?: boolean;
  title?: string;
  description?: string;
  content?: string;
  email?: string;
  phone?: string;
  whatsapp?: string;
  working_hours?: string;
  show_inquiry_form?: boolean;
  [key: string]: unknown;
}

interface Clinic {
  id: string;
  slug: string;
  clinic_name_ar: string;
  specialist_id: string;
  logo_url: string | null;
  colors: Record<string, unknown>;
  custom_pages: Record<string, PageConfig> | null;
  template_id: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  whatsapp_number: string | null;
  settings: Record<string, unknown>;
}

interface ClinicCustomNavigationState {
  clinicData?: Clinic | null;
  clinicCache?: Record<string, unknown> | null;
}

const inquirySchema = z.object({
  name: z.string().trim().min(2, "الاسم مطلوب (حرفين على الأقل)").max(100),
  email: z.string().trim().email("البريد الإلكتروني غير صحيح").max(255),
  phone: z.string().optional(),
  subject: z.string().trim().min(3, "الموضوع مطلوب (3 أحرف على الأقل)").max(200),
  message: z.string().trim().min(10, "الرسالة مطلوبة (10 أحرف على الأقل)").max(2000),
});

const ClinicCustomPage = () => {
  const { slug, pageType } = useParams<{ slug: string; pageType: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const navigationState = location.state as ClinicCustomNavigationState | null;
  const cachedClinicPayload = useMemo(
    () => navigationState?.clinicCache ?? readClinicPublicCache<Record<string, unknown>>(slug),
    [navigationState, slug]
  );

  const [clinic, setClinic] = useState<Clinic | null>(() => navigationState?.clinicData ?? (cachedClinicPayload?.clinic as Clinic | null) ?? null);
  const [loading, setLoading] = useState(() => !navigationState?.clinicData && !(cachedClinicPayload?.clinic as Clinic | null));
  const [notFound, setNotFound] = useState(false);
  const [pageNotEnabled, setPageNotEnabled] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const fetchClinic = async () => {
      if (!slug) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      const { data: clinicDataArr, error } = await supabase
        .rpc('get_public_clinic_info', { _slug: slug.toLowerCase() });

      const clinicData = clinicDataArr?.[0] || null;

      if (error || !clinicData) {
        if (navigationState?.clinicData || cachedClinicPayload?.clinic) {
          setLoading(false);
          return;
        }
        setNotFound(true);
        setLoading(false);
        return;
      }

      const customPages = clinicData.custom_pages;
      if (!customPages || !customPages[pageType as string]?.enabled) {
        setPageNotEnabled(true);
        setClinic(clinicData as unknown as Clinic);
        writeClinicPublicCache(slug, {
          ...(readClinicPublicCache<Record<string, unknown>>(slug) || {}),
          clinic: clinicData,
        });
        setLoading(false);
        return;
      }

      setClinic(clinicData as unknown as Clinic);
      writeClinicPublicCache(slug, {
        ...(readClinicPublicCache<Record<string, unknown>>(slug) || {}),
        clinic: clinicData,
      });
      setLoading(false);
    };

    fetchClinic();
  }, [cachedClinicPayload, navigationState?.clinicData, pageType, slug]);

  const primaryColor = (clinic?.colors?.primary as string) || '#8a8a8a';
  const isArabic = ((clinic?.settings?.language as string) || 'ar') === 'ar';

  const submitInquiryMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      // Validate
      const result = inquirySchema.safeParse(data);
      if (!result.success) {
        const errors: Record<string, string> = {};
        result.error.errors.forEach((err) => {
          if (err.path[0]) {
            errors[err.path[0] as string] = err.message;
          }
        });
        throw { validationErrors: errors };
      }

      const { error } = await supabase
        .from("clinic_visitor_inquiries")
        .insert({
          clinic_id: clinic!.id,
          name: data.name.trim(),
          email: data.email.trim(),
          phone: data.phone?.trim() || null,
          subject: data.subject.trim(),
          message: data.message.trim(),
        });

      if (error) throw error;
    },
    onSuccess: () => {
      setFormSubmitted(true);
      toast.success("تم إرسال استفسارك بنجاح");
    },
    onError: (error: Error & { validationErrors?: Record<string, string> }) => {
      if (error.validationErrors) {
        setFormErrors(error.validationErrors);
      } else {
        toast.error("حدث خطأ أثناء إرسال الاستفسار");
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormErrors({});
    submitInquiryMutation.mutate(formData);
  };

  const getPageConfig = () => {
    if (!clinic?.custom_pages || !pageType) return null;
    return clinic.custom_pages[pageType];
  };

  const pageConfig = getPageConfig();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-gray-200 rounded-full animate-spin" style={{ borderTopColor: primaryColor }}></div>
          <p className="text-muted-foreground">{isArabic ? 'جاري التحميل...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  if (notFound || !clinic) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50" dir="rtl">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="pt-8 pb-8 text-center">
            <h1 className="text-2xl font-bold mb-2">{isArabic ? 'الصفحة غير موجودة' : 'Page Not Found'}</h1>
            <p className="text-muted-foreground mb-6">{isArabic ? 'عذراً، لم نتمكن من إيجاد الصفحة المطلوبة' : 'Sorry, we could not find the requested page'}</p>
            <Button onClick={() => navigate('/')}>{isArabic ? 'العودة للرئيسية' : 'Back to Home'}</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (pageNotEnabled) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50" dir="rtl">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="pt-8 pb-8 text-center">
            <h1 className="text-2xl font-bold mb-2">{isArabic ? 'الصفحة غير متاحة' : 'Page Not Available'}</h1>
            <p className="text-muted-foreground mb-6">{isArabic ? 'هذه الصفحة غير مفعّلة حالياً' : 'This page is not currently enabled'}</p>
            <Button onClick={() => navigate(`/clinic/${slug}`)}>{isArabic ? 'العودة للعيادة' : 'Back to Clinic'}</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const renderPageContent = () => {
    switch (pageType) {
      case "contact":
        return renderContactPage();
      case "about":
        return renderAboutPage();
      case "privacy":
        return renderPrivacyPage();
      case "refund":
        return renderRefundPage();
      case "testimonials":
        return renderTestimonialsPage();
      default:
        return null;
    }
  };

  const renderContactPage = () => {
    const contactConfig = pageConfig;

    return (
      <div className="space-y-8">
        {/* Contact Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {(contactConfig.email || clinic.contact_email) && (
            <Card className="text-center">
              <CardContent className="pt-6">
                <div 
                  className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center"
                  style={{ backgroundColor: `${primaryColor}15` }}
                >
                  <Mail className="h-6 w-6" style={{ color: primaryColor }} />
                </div>
                <h4 className="font-medium mb-1">{isArabic ? 'البريد الإلكتروني' : 'Email'}</h4>
                <a 
                  href={`mailto:${contactConfig.email || clinic.contact_email}`}
                  className="text-sm text-muted-foreground hover:underline"
                  dir="ltr"
                >
                  {contactConfig.email || clinic.contact_email}
                </a>
              </CardContent>
            </Card>
          )}

          {(contactConfig.phone || clinic.contact_phone) && (
            <Card className="text-center">
              <CardContent className="pt-6">
                <div 
                  className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center"
                  style={{ backgroundColor: `${primaryColor}15` }}
                >
                  <Phone className="h-6 w-6" style={{ color: primaryColor }} />
                </div>
                <h4 className="font-medium mb-1">{isArabic ? 'رقم الهاتف' : 'Phone'}</h4>
                <a 
                  href={`tel:${contactConfig.phone || clinic.contact_phone}`}
                  className="text-sm text-muted-foreground hover:underline"
                  dir="ltr"
                >
                  {contactConfig.phone || clinic.contact_phone}
                </a>
              </CardContent>
            </Card>
          )}

          {(contactConfig.whatsapp || clinic.whatsapp_number) && (
            <Card className="text-center">
              <CardContent className="pt-6">
                <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center bg-green-50">
                  <MessageCircle className="h-6 w-6 text-green-600" />
                </div>
                <h4 className="font-medium mb-1">{isArabic ? 'واتساب' : 'WhatsApp'}</h4>
                <a 
                  href={`https://wa.me/${contactConfig.whatsapp || clinic.whatsapp_number}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-green-600 hover:underline"
                  dir="ltr"
                >
                  {contactConfig.whatsapp || clinic.whatsapp_number}
                </a>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Working Hours */}
        {contactConfig.working_hours && (
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: `${primaryColor}15` }}
                >
                  <Clock className="h-5 w-5" style={{ color: primaryColor }} />
                </div>
                <div>
                  <h4 className="font-medium">{isArabic ? 'ساعات العمل' : 'Working Hours'}</h4>
                  <p className="text-muted-foreground">{contactConfig.working_hours}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Inquiry Form */}
        {contactConfig.show_inquiry_form && (
          <Card>
            <CardHeader>
              <CardTitle>{isArabic ? 'أرسل استفسارك' : 'Send Your Inquiry'}</CardTitle>
            </CardHeader>
            <CardContent>
              {formSubmitted ? (
                <div className="text-center py-8">
                  <div 
                    className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                    style={{ backgroundColor: `${primaryColor}15` }}
                  >
                    <CheckCircle className="h-8 w-8" style={{ color: primaryColor }} />
                  </div>
                   <h3 className="text-xl font-bold mb-2">{isArabic ? 'تم إرسال استفسارك بنجاح!' : 'Your inquiry has been sent successfully!'}</h3>
                   <p className="text-muted-foreground mb-4">
                     {isArabic ? 'سيتم الرد عليك في أقرب وقت ممكن' : 'We will get back to you as soon as possible'}
                   </p>
                   <Button 
                     variant="outline"
                     onClick={() => {
                       setFormSubmitted(false);
                       setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
                     }}
                   >
                     {isArabic ? 'إرسال استفسار آخر' : 'Send Another Inquiry'}
                   </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                     <Label>{isArabic ? 'الاسم الكامل *' : 'Full Name *'}</Label>
                     <Input
                       value={formData.name}
                       onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                       placeholder={isArabic ? 'أدخل اسمك' : 'Enter your name'}
                       className={formErrors.name ? "border-red-500" : ""}
                      />
                      {formErrors.name && (
                        <p className="text-sm text-red-500">{formErrors.name}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>{isArabic ? 'البريد الإلكتروني *' : 'Email *'}</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="example@email.com"
                        dir="ltr"
                        className={formErrors.email ? "border-red-500" : ""}
                      />
                      {formErrors.email && (
                        <p className="text-sm text-red-500">{formErrors.email}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{isArabic ? 'رقم الجوال (اختياري)' : 'Phone (optional)'}</Label>
                      <Input
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="05xxxxxxxx"
                        dir="ltr"
                      />
                    </div>
                    <div className="space-y-2">
                     <Label>{isArabic ? 'الموضوع *' : 'Subject *'}</Label>
                     <Input
                       value={formData.subject}
                       onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                       placeholder={isArabic ? 'موضوع الاستفسار' : 'Inquiry subject'}
                        className={formErrors.subject ? "border-red-500" : ""}
                      />
                      {formErrors.subject && (
                        <p className="text-sm text-red-500">{formErrors.subject}</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                     <Label>{isArabic ? 'الرسالة *' : 'Message *'}</Label>
                     <Textarea
                       value={formData.message}
                       onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                       placeholder={isArabic ? 'اكتب استفسارك هنا...' : 'Write your inquiry here...'}
                       rows={5}
                      className={formErrors.message ? "border-red-500" : ""}
                    />
                    {formErrors.message && (
                      <p className="text-sm text-red-500">{formErrors.message}</p>
                    )}
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full gap-2 text-white"
                    style={{ backgroundColor: primaryColor }}
                    disabled={submitInquiryMutation.isPending}
                  >
                    <Send className="h-4 w-4" />
                    {submitInquiryMutation.isPending ? (isArabic ? 'جاري الإرسال...' : 'Sending...') : (isArabic ? 'إرسال الاستفسار' : 'Send Inquiry')}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  const renderAboutPage = () => (
    <Card>
      <CardContent className="pt-6">
        <div className="prose prose-lg max-w-none" dir="rtl">
          {pageConfig.content ? (
            <div className="whitespace-pre-wrap text-muted-foreground leading-relaxed">
              {pageConfig.content}
            </div>
          ) : (
           <p className="text-muted-foreground text-center py-8">
              {isArabic ? 'لم يتم إضافة محتوى لهذه الصفحة بعد' : 'No content has been added to this page yet'}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );

  const renderPrivacyPage = () => (
    <Card>
      <CardContent className="pt-6">
        <div className="prose prose-lg max-w-none" dir="rtl">
          {pageConfig.content ? (
            <div className="whitespace-pre-wrap text-muted-foreground leading-relaxed">
              {pageConfig.content}
            </div>
          ) : (
           <p className="text-muted-foreground text-center py-8">
              {isArabic ? 'لم يتم إضافة سياسة الخصوصية بعد' : 'Privacy policy has not been added yet'}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );

  const renderRefundPage = () => (
    <Card>
      <CardContent className="pt-6">
        <div className="prose prose-lg max-w-none" dir="rtl">
          {pageConfig.content ? (
            <div className="whitespace-pre-wrap text-muted-foreground leading-relaxed">
              {pageConfig.content}
            </div>
          ) : (
           <p className="text-muted-foreground text-center py-8">
              {isArabic ? 'لم يتم إضافة سياسة الاسترجاع بعد' : 'Refund policy has not been added yet'}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );

  const renderTestimonialsPage = () => {
    return (
      <ClinicTestimonialsSection
        clinicId={clinic!.id}
        specialistId={clinic!.specialist_id}
        clinicName={clinic!.clinic_name_ar}
        primaryColor={primaryColor}
        isArabic={isArabic}
        pageConfig={pageConfig}
      />
    );
  };

  return (
    <div className="min-h-screen" dir={isArabic ? 'rtl' : 'ltr'} style={{ background: `linear-gradient(to bottom, ${primaryColor}0D, transparent)` }}>
      {/* Header */}
      <div style={{ backgroundColor: primaryColor }}>
        <div className="container mx-auto px-4 max-w-4xl py-12">
          <Button
            variant="ghost"
            className="text-white/90 hover:text-white hover:bg-white/10 mb-4 gap-2"
            onClick={() => navigate(`/clinic/${slug}`, {
              state: {
                clinicData: clinic,
                clinicCache: readClinicPublicCache<Record<string, unknown>>(slug),
              }
            })}
          >
            <ArrowRight className="h-4 w-4" />
            {isArabic ? 'الرئيسية' : 'Home'}
          </Button>

          <div className="flex items-center gap-4">
            <Avatar className="h-16 w-16 border-2 border-white/30 bg-white shadow-sm">
              <AvatarImage src={clinic.logo_url || undefined} className="object-contain p-1" />
              <AvatarFallback
                className="text-2xl text-white font-bold"
                style={{ backgroundColor: `${primaryColor}cc` }}
              >
                {clinic.clinic_name_ar?.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="text-white/70 text-sm">{clinic.clinic_name_ar}</p>
              <h1 className="text-2xl md:text-3xl font-bold text-white">
                {pageConfig?.title || (isArabic ? 'الصفحة' : 'Page')}
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 max-w-4xl py-8">
      {pageConfig?.description && (pageType === "contact" || pageType === "testimonials") && (
          <p className="text-center text-muted-foreground mb-8 text-lg">
            {pageConfig.description}
          </p>
        )}
        {renderPageContent()}
      </div>
    </div>
  );
};

export default ClinicCustomPage;
